import {
  buildProxiedInstance,
  dragAndDropFeature,
  expandAllFeature,
  hotkeysCoreFeature,
  ItemInstance,
  keyboardDragAndDropFeature,
  selectionFeature,
  syncDataLoaderFeature,
  TreeInstance,
} from '@headless-tree/core';
import { AssistiveTreeDescription, useTree } from '@headless-tree/react';
import { useVirtualizer, Virtualizer } from '@tanstack/react-virtual';
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';
import { useClickOutside } from 'shared/hooks/use-click-outside';
import { ID } from 'shared/types/id';

import { getItemPath, transformToInternalTreeItems } from './get-item-path';
import { InternalTreeItem, TreeItem, TreeItemID, TreeItemTypes } from './types';
import { useExpandedItems } from './use-expanded-items';
import { useKeyboardShortcuts } from './use-keyboard-shortcuts';
import { useMultiSelect } from './use-multi-select';
import { useOnDrop } from './use-on-drop';
import * as Styled from './virtualized-tree-list.style';

const defaultTreeItemTypes = Object.values(TreeItemTypes);
const defaultDropTypes: Record<TreeItemTypes, TreeItemTypes[]> = {
  [TreeItemTypes.Root]: [
    TreeItemTypes.Goal,
    TreeItemTypes.LifeArea,
    TreeItemTypes.Priority,
    TreeItemTypes.Date,
    TreeItemTypes.DayPart,
    TreeItemTypes.Tag,
  ],
  [TreeItemTypes.Goal]: [TreeItemTypes.Task, TreeItemTypes.Goal],
  [TreeItemTypes.LifeArea]: [TreeItemTypes.Task, TreeItemTypes.Goal],
  [TreeItemTypes.Priority]: [TreeItemTypes.Task],
  [TreeItemTypes.Date]: [TreeItemTypes.Task],
  [TreeItemTypes.DayPart]: [TreeItemTypes.Task],
  [TreeItemTypes.Tag]: [TreeItemTypes.Task],
  [TreeItemTypes.Task]: [],
};

export type RenderItemProps = {
  item: InternalTreeItem;
  itemInstance: ItemInstance<InternalTreeItem>;
  tree: TreeInstance<InternalTreeItem>;
};

type ListProps = {
  tree: TreeInstance<InternalTreeItem>;
  renderItem: React.FC<RenderItemProps>;
  scrollElement?: React.RefObject<HTMLDivElement>;
  clearSelection?: () => void;
};

const List = forwardRef<Virtualizer<HTMLDivElement, Element>, ListProps>(
  ({ tree, renderItem: Item, scrollElement, clearSelection }, ref) => {
    const _parentRef = useRef<HTMLDivElement>(null);
    const parentRef = scrollElement ?? _parentRef;

    const VirtualContainer = scrollElement
      ? React.Fragment
      : Styled.VirtualizerContainer;
    const virtualContainerProps = scrollElement
      ? {}
      : { ref: parentRef, $scrollable: true };

    const virtualizer = useVirtualizer({
      count: tree.getItems().length,
      getScrollElement: () => parentRef.current,
      estimateSize: () => 60,
    });

    useImperativeHandle(ref, () => virtualizer);

    useClickOutside(parentRef, clearSelection);

    return (
      // @ts-ignore
      <VirtualContainer {...virtualContainerProps}>
        <Styled.TreeContainer
          {...tree.getContainerProps()}
          $height={virtualizer.getTotalSize()}
        >
          {virtualizer.getVirtualItems().map((virtualItem) => {
            const item = tree.getItems()[virtualItem.index];
            const itemProps = item.getProps();
            return (
              <Styled.ItemContainer
                {...itemProps}
                key={virtualItem.key}
                data-index={virtualItem.index}
                ref={(r) => {
                  virtualizer.measureElement(r);
                  itemProps.ref(r);
                }}
                $level={item.getItemMeta().level}
                className="fc-draggable-event"
                style={{
                  transform: `translateY(${virtualItem.start}px)`,
                }}
              >
                <Item
                  item={item.getItemData()}
                  itemInstance={item}
                  tree={tree}
                />
              </Styled.ItemContainer>
            );
          })}
          <Styled.DragLine style={tree.getDragLineStyle()} />
        </Styled.TreeContainer>
      </VirtualContainer>
    );
  },
);

export type VirtualizedTreeListProps = {
  items: Record<ID, TreeItem>;
  rootItemId: ID;
  renderItem: React.FC<RenderItemProps>;
  scrollElement?: React.RefObject<HTMLDivElement>;
  multiselectTypes?: TreeItemTypes[];
  dragTypes?: TreeItemTypes[];
  dropTypes?: Record<TreeItemTypes, TreeItemTypes[]>;
  onReorder?: (updatedTreeItems: TreeItem[]) => void;
  collapsedItems?: ID[];
  onCollapse?: (ids: ID[]) => void;
};

export const VirtualizedTreeList: React.FC<VirtualizedTreeListProps> = ({
  items,
  rootItemId,
  renderItem,
  scrollElement,
  multiselectTypes = defaultTreeItemTypes,
  dragTypes = defaultTreeItemTypes,
  dropTypes = defaultDropTypes,
  collapsedItems = [],
  onCollapse,
  onReorder,
}) => {
  // Transform items to InternalTreeItem with path-based IDs
  const internalItems = useMemo(
    () => transformToInternalTreeItems(items, rootItemId),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- items are recalculated in useEffect
    [],
  );

  const _items = useRef(internalItems);
  const virtualizer = useRef<Virtualizer<HTMLDivElement, Element> | null>(null);

  // feature support
  const { selectedItems, setSelectedItems, clearSelection } = useMultiSelect(
    _items.current,
    multiselectTypes,
  );
  const onDrop = useOnDrop(_items.current, onReorder);
  const { expandedItems, setExpandedItems } = useExpandedItems(
    _items.current,
    collapsedItems,
    onCollapse,
  );

  const tree = useTree<InternalTreeItem>({
    state: {
      selectedItems,
      expandedItems,
    },
    setSelectedItems,
    setExpandedItems,
    rootItemId: getItemPath(rootItemId), // Root path is just the root ID
    isItemFolder: (item) => !!item.getItemData().isFolder,
    getItemName: (item) => item.getItemData().name,
    dataLoader: {
      getItem: (itemId) => _items.current?.[itemId as TreeItemID],
      getChildren: (itemId) =>
        _items.current?.[itemId as TreeItemID]?.children ?? [],
    },
    scrollToItem: (item) =>
      virtualizer.current?.scrollToIndex(item.getItemMeta().index),
    onDrop,
    canDrag: (selectedItems) =>
      selectedItems.every((item) =>
        dragTypes.includes(item.getItemData().type),
      ),
    canDrop: (items, target) => {
      const allowedTypes = dropTypes[target.item?.getItemData().type];
      if (!allowedTypes?.length) {
        return false;
      }

      return items.every((item) =>
        allowedTypes.includes(item.getItemData().type),
      );
    },
    canReorder: true,
    instanceBuilder: buildProxiedInstance,
    features: [
      syncDataLoaderFeature,
      selectionFeature,
      dragAndDropFeature,
      keyboardDragAndDropFeature,
      expandAllFeature,
      hotkeysCoreFeature,
    ],
  });

  // keyboard shortcuts
  useKeyboardShortcuts(items, { onCollapse });

  useEffect(() => {
    _items.current = transformToInternalTreeItems(items, rootItemId);
    tree.rebuildTree();
  }, [items, rootItemId, tree]);

  return (
    <>
      <AssistiveTreeDescription tree={tree} />
      <List
        tree={tree}
        ref={virtualizer}
        renderItem={renderItem}
        scrollElement={scrollElement}
        clearSelection={clearSelection}
      />
    </>
  );
};
